/* eslint-disable complexity */
/* eslint-disable sonarjs/cognitive-complexity */
import merge from 'deepmerge';
import type { ECharts } from 'echarts';
import { Option } from '../../core/option';
import { containBubble } from '../dvScatter';
import StandardChart from '../../core/StandardChart';
import { optionDelayHandle, axisRangeHandle } from './util';

interface Point {
  seriesIndex?: number;
  dataIndex?: number;
  // name?: string;
}

interface Config {
  autoAdapt?: boolean;
  xNiceRangeConfig?: {
    absoluteInterval?: number;
    relativeInterval?: number;
  };
  yNiceRangeConfig?: {
    absoluteInterval?: number;
    relativeInterval?: number;
  };
  axisRangeMerge: 'both' | 'custom' | 'helper';
}

/* 播放动画duration */
const PLAY_ANIMATION_DURATION = 2000;

/* 默认形变动画duration */
const NORMAL_ANIMATION_DURATION = 300;

/* 步进动画duration */
const STEP_ANIMATION_DURATION = 300;

/* 播放动画Easing */
const PLAY_ANIMATION_EASING = 'linear';

/* 默认动画Easing */
const TRANSITION_ANIMATION_EASING = 'cubicOut';

const HISTORY_OPACITY_PERCENT = 0.5;

const X_INDEX = 0;
const Y_INDEX = 1;
const R_INDEX = 2;

export class DynamicDvScatterHelper {
  /* 多周期配置项 */
  protected _options: Option[];

  /* 历史轨迹用配置项,防止污染源数据 */
  protected _historyOptions: Option[];

  /* helper配置项 */
  protected _config: Config = {
    autoAdapt: true,
    xNiceRangeConfig: {
      relativeInterval: 2
    },
    yNiceRangeConfig: {
      relativeInterval: 2
    },
    axisRangeMerge: 'both'
  };

  /* standChart实例 */
  protected _chart: StandardChart;

  /* 挂载的Dom */
  protected _dom: HTMLDivElement;

  /* 主题 */
  protected _themeName: string;

  /* timeline实例 */
  protected _timeline: unknown;

  /* echarts 实例 */
  protected _ecIns: ECharts;

  /* zrender 实例 */
  protected _zrIns: unknown;

  /* isInit 是否已经实例化的标记位 */
  protected _isInit = false;

  /* select Point 非历史轨迹下的选中点记录 */
  protected _selectPoint: Point[] = [];

  /* 时间轴暂停的时间戳，用于动画 */
  protected _pauseAnimationTimer;

  /* 历史轨迹高亮最后一个点的时间戳，用于动画 */
  protected _lastPointAnimationTimer;

  /* 时间轴的timeInterval，取出用作duration */
  protected _playDuration: number;

  /* 历史轨迹插件，允许业务方传入配置项更改历史轨迹的行为 */
  protected _historySeriesPlugin: Record<string, (...args: unknown[]) => unknown> = {};

  /* 历史轨迹模板，包含全周期历史轨迹的series数组，与selectPoint对应 */
  protected _historySeriesTemplate = [];

  /* 标记是否处于历史轨迹装态 */
  protected _isHistory = false;

  /* 记录默认的轴线极值，用于还原Adapt */
  protected _defaultAxisRange: {
    xRange: [number, number];
    yRange: [number, number];
  };

  /* 记录历史轨迹下的轴线极值，用于在历史轨迹下还原Adapt，仅在开启autoAdapt后启用 */
  protected _historyAxisRange: {
    xRange: [number, number];
    yRange: [number, number];
  };

  /* 记录全周期SymbolSize */
  protected _symbolSize: [number, number];

  /* 记录全周期RValue极值 */
  protected _rValueRange: [number, number];

  constructor(options: Option[], chart: StandardChart, timeline: unknown, config: Config) {
    this._options = merge([], options);
    this._historyOptions = merge([], options);
    this._chart = chart;
    this._dom = chart.getDom() as HTMLDivElement;
    this._themeName = chart.getModel().getThemeName();
    this._timeline = timeline;
    this._playDuration =
      this._timeline?._option?.config?.animation?.intervalTime ?? PLAY_ANIMATION_DURATION;
    this._config = merge(this._config, config || {});
    // 绑定交互以及初始化ecIns与zrIns
    this._actionHandle();
    // 预处理series
    this._preprocessOptions();
    // 渲染指定周期数据
    this._init();
  }

  // 渲染指定周期数据
  protected _init() {
    const currentIndex = this._timeline.getCurrentIndex();
    this._chart.play({
      option: merge(this._options[currentIndex], {
        animationDuration: NORMAL_ANIMATION_DURATION,
        animationDurationUpdate: NORMAL_ANIMATION_DURATION
      })
    });
  }

  // 协助处理多周期配置项
  protected _preprocessOptions() {
    // part0 获取全周期的symbolSize与rValueRange
    const symbolSize = this._getSymbolSize();
    if (symbolSize) {
      this._symbolSize = symbolSize;
    }
    const flatValue = this._getFlatValue();
    const rValueRange: [number, number] = [Infinity, -Infinity];
    flatValue.forEach(value => {
      if (value[R_INDEX] > rValueRange[1]) {
        rValueRange[1] = value[R_INDEX];
      }
      if (value[R_INDEX] < rValueRange[0]) {
        rValueRange[0] = value[R_INDEX];
      }
    });
    this._rValueRange = rValueRange;
    // part1 计算轴线极值
    // part1 step1 尝试获取外部轴线极值
    const currentIndex = this._timeline.getCurrentIndex();
    const xAxisOption = this._options[currentIndex].xAxis;
    const yAxisOption = this._options[currentIndex].yAxis;
    const xAxisItemOption = Array.isArray(xAxisOption) ? xAxisOption[0] : xAxisOption;
    const yAxisItemOption = Array.isArray(yAxisOption) ? yAxisOption[0] : yAxisOption;
    const { min: xMin, max: xMax } = xAxisItemOption || {};
    const { min: yMin, max: yMax } = yAxisItemOption || {};
    const customXRange = [xMin, xMax];
    const customYRange = [yMin, yMax];
    // part1 step2 计算内部轴线极值
    const { xRange: helperXRValueRange, yRange: helperYRValueRange } = containBubble(
      flatValue,
      this._dom,
      symbolSize || this._themeName,
      {
        xNiceRangeConfig: this._config.xNiceRangeConfig,
        yNiceRangeConfig: this._config.yNiceRangeConfig
      }
    );
    // part1 step3 根据合并策略合并外部轴线极值与内部轴线极值
    const { axisRangeMerge } = this._config;
    let xRange;
    let yRange;
    if (axisRangeMerge === 'custom') {
      xRange = [customXRange[0] ?? helperXRValueRange[0], customXRange[1] ?? helperXRValueRange[1]];
      yRange = [customYRange[0] ?? helperYRValueRange[0], customYRange[1] ?? helperYRValueRange[1]];
    } else if (axisRangeMerge === 'helper') {
      xRange = helperXRValueRange;
      yRange = helperYRValueRange;
    } else {
      xRange = [
        Math.min(customXRange[0] ?? Infinity, helperXRValueRange[0]),
        Math.max(customXRange[1] ?? -Infinity, helperXRValueRange[1])
      ];
      yRange = [
        Math.min(customYRange[0] ?? Infinity, helperYRValueRange[0]),
        Math.max(customYRange[1] ?? -Infinity, helperYRValueRange[1])
      ];
    }
    this._defaultAxisRange = { xRange, yRange };
    // part2 预处理配置项
    // 更新每个周期的轴线极值为总周期的极值
    axisRangeHandle(this._options, { xRange, yRange });

    this._options.forEach(option => {
      // 更新每个series的dvRValueRange以统一比例尺，使得任一周期任一气泡的数值与半径的比例关系不变
      option.series.forEach(seriesItem => {
        if (seriesItem.type !== 'dvScatter') {
          return;
        }
        seriesItem.dvRValueRange = rValueRange;
      });
    });
  }

  // 尝试获取全周期的symbolSize
  protected _getSymbolSize() {
    const firstDvScatter = this._options[0].series.find(item => item.type === 'dvScatter');
    if (firstDvScatter && firstDvScatter.symbolSize) {
      return firstDvScatter.symbolSize;
    }
    return null;
  }

  // 获取全周期拍平后的数据
  protected _getFlatValue() {
    const result = [];
    this._options.forEach(option => {
      const { series } = option;
      series.forEach(seriesItem => {
        if (seriesItem.type !== 'dvScatter') {
          return;
        }
        const { data } = seriesItem;
        data.forEach(dataItem => {
          result.push(dataItem.value || data);
        });
      });
    });
    return result;
  }

  // 交互处理
  protected _actionHandle() {
    this._chart.on('dv:afterupdate', () => {
      if (!this._isInit) {
        this._ecIns = this._chart.getECharts();
        this._zrIns = this._ecIns.getZr();
        this._ecInsActionHandle();
        this._zrInsActionHandle();
        this._timelineActionHandle();
        this._isInit = true;
      }
    });
  }

  // echarts实例交互处理
  protected _ecInsActionHandle() {
    // 选择与非选择时更新数据
    this._ecIns.on('dvselect', params => {
      if (this._isHistory) {
        return;
      }
      const seriesIndex = params.seriesIndex as number;
      const dataIndex = params.dataIndex as number;

      // 系列未开启多选时，删除前面的选中点
      const currentIndex = this._timeline.getCurrentIndex();
      const currentOption = this._isHistory
        ? this._historyOptions[currentIndex]
        : this._options[currentIndex];
      const seriesTemp = currentOption.series[seriesIndex];
      if (!seriesTemp.dvSelectMultiple) {
        this._selectPoint = [];
      }

      this._selectPoint.push({ seriesIndex, dataIndex });
    });
    this._ecIns.on('dvunselect', params => {
      const { seriesIndex } = params;
      if (seriesIndex) {
        this._selectPoint = this._selectPoint.filter(
          item => !seriesIndex.includes(item.seriesIndex)
        );
      } else {
        this._selectPoint = [];
      }
    });
  }

  // zrender实例交互处理
  protected _zrInsActionHandle() {
    this._zrIns.on('click', event => {
      // 时间轴播放时点击画布暂停
      this._timeline.isPlaying() && this._timeline.pause();
    });
  }

  // timeline实例交互处理
  protected _timelineActionHandle() {
    this._timeline.on('change', params => {
      this._timelineChangeHandle(params);
    });

    this._timeline.on('pause', () => {
      this._timelinePauseHandle();
    });

    this._timeline.on('play', () => {
      this._timelinePlayHandle();
    });
  }

  // play helper of _timelineActionHandle
  protected _timelinePlayHandle() {
    clearTimeout(this._pauseAnimationTimer);
    clearTimeout(this._lastPointAnimationTimer);
    // 切换动画时长为播放态
    const currentIndex = this._timeline.getCurrentIndex();
    const currentOption = this._isHistory
      ? this._historyOptions[currentIndex]
      : this._options[currentIndex];
    this._chart.play({
      option: merge(currentOption, {
        animationDuration: this._playDuration,
        animationDurationUpdate: this._playDuration,
        animationEasing: PLAY_ANIMATION_EASING,
        animationEasingUpdate: PLAY_ANIMATION_EASING
      })
    });
  }

  // pause helper of _timelineActionHandle
  protected _timelinePauseHandle() {
    // 等待最后一个周期动画结束，切换动画时长为常态
    this._pauseAnimationTimer = setTimeout(() => {
      const currentIndex = this._timeline.getCurrentIndex();
      const currentOption = this._isHistory
        ? this._historyOptions[currentIndex]
        : this._options[currentIndex];
      this._chart.play({
        option: merge(currentOption, {
          animationDuration: NORMAL_ANIMATION_DURATION,
          animationDurationUpdate: NORMAL_ANIMATION_DURATION,
          animationEasing: TRANSITION_ANIMATION_EASING,
          animationEasingUpdate: TRANSITION_ANIMATION_EASING
        })
      });
    }, this._playDuration);
  }

  // change helper of _timelineActionHandle
  protected _timelineChangeHandle(params: any) {
    const { index } = params;

    // 刷新高亮态
    this._ecIns.dispatchAction({
      type: 'dvUnselect'
    });

    if (!this._isHistory) {
      // 非历史轨迹下，切换周期时更新数据
      // TODO: 清空当前没有数据的series
      this._chart.play({
        option: merge(this._options[index], {
          animationDuration: this._playDuration,
          animationDurationUpdate: this._playDuration,
          animationEasing: PLAY_ANIMATION_EASING,
          animationEasingUpdate: PLAY_ANIMATION_EASING
        }),
        opts: {
          notMerge: 'series'
        }
      });
    } else {
      this._chart.play({
        option: this._historyOptions[index]
      });
      // 高亮所有系列的最后一个点
      this._highlightLastPoint(this._historyOptions[index]);
    }
  }

  // 获取历史轨迹series模板（包含全周期历史数据的series）
  protected _getHistorySeriesTemplate() {
    // 重置historyOptions
    this._historyOptions = merge([], this._options);
    const currentIndex = this._timeline.getCurrentIndex();
    const currentOption = merge({}, this._options[currentIndex]);
    const historySeriesArr = [];
    this._selectPoint.forEach(item => {
      const { seriesIndex, dataIndex } = item;

      const currentSeriesTemp = currentOption.series[seriesIndex];
      // step 0, 完善基本信息
      // 因为多周期中index可能会改变，所以需要优先根据name匹配
      const currentSeriesName = currentSeriesTemp.name;
      const currentDataName = currentSeriesTemp.data[dataIndex].name;
      // 记录高亮气泡的样式，当气泡本身使用ECharts默认值时，使用该样式，避免新系列匹配到新的颜色
      const seriesModel = this._ecIns.getModel().getSeriesByIndex(seriesIndex);
      const pointElement = seriesModel.getData().getItemGraphicEl(dataIndex);
      let pointStyle = pointElement.style || {};
      if (pointElement.isGroup) {
        pointElement.traverse(childEl => {
          // 寻找主图元
          const { isMain = false } = childEl.extra || {};
          if (isMain) {
            // 寻找常态样式
            pointStyle = childEl.currentStates.length ? childEl._normalState.style : childEl.style;
          }
        });
      }

      // step 1, 查询历史信息，组合itemStyle与itemConfig，生成包含全周期数据的历史轨迹数据
      // 对应周期的历史数据从该全周期历史数据中截出
      // 全周期历史数据
      const historyData = [];
      // 从原始数据查询，找到目标后深拷贝
      this._historyOptions.forEach((option, index) => {
        // 标记是否找到目标
        let isFindTarget = false;
        const seriesTemp = Array.isArray(option.series) ? option.series : [option.series];
        const targetSeries = currentSeriesName
          ? seriesTemp.find(series => series.name === currentSeriesName)
          : seriesTemp[seriesIndex];
        if (targetSeries) {
          const targetSeriesIndex = seriesTemp.findIndex(d => d === targetSeries);
          const dataTemp = Array.isArray(targetSeries.data)
            ? targetSeries.data
            : [targetSeries.data];
          const targetData = currentDataName
            ? dataTemp.find(data => data.name === currentDataName)
            : dataTemp[dataIndex];
          if (targetData) {
            const targetDataIndex = dataTemp.findIndex(d => d === targetData);
            // 记录目标data信息
            historyData.push(
              merge(
                {
                  itemStyle: targetSeries.dvItemStyle || [],
                  itemConfig: targetSeries.dvItemConfig || {},
                  originTimelineIndex: index,
                  originSeriesIndex: targetSeriesIndex,
                  originDataIndex: targetDataIndex
                },
                targetData
              )
            );
            // 记录后删除该点
            dataTemp.splice(dataIndex, 1);
            isFindTarget = true;
          }
        }
        // 未找到目标则添加为空
        if (!isFindTarget) {
          historyData.push(null);
        }
      });

      // step2, 生成历史轨迹的series模板，单周期series即为该模板中data截取后的结果
      const itemVisual = {
        fill: pointStyle.fill,
        stroke: pointStyle.stroke,
        opacity: (pointStyle.opacity || 1) * HISTORY_OPACITY_PERCENT,
        lineWidth: pointStyle.lineWidth
      };
      const historySeries = {
        type: 'dvScatter',
        // 历史轨迹series标记位
        isHistory: true,

        // 唯一ID
        id: currentDataName
          ? `${currentDataName}-history`
          : `seriesIdx${seriesIndex}-dataIdx${dataIndex}-history`,
        name: currentDataName
          ? `${currentDataName}-history`
          : `seriesIdx${seriesIndex}-dataIdx${dataIndex}-history`,
        // 历史轨迹的data
        data: historyData,
        // 全周期rValueRange
        dvRValueRange: this._rValueRange,
        // 继承单点visual,blur态样式采用常态样式
        dvItemStyle: [
          {
            id: 'circle',
            style: itemVisual,
            blur: { style: itemVisual }
          }
        ],
        // 开启keepHighlight,历史轨迹必须有选中点
        dvKeepHighlight: true
      };
      let historySeriesTemp = historySeries;
      const historySeriesPluginItem = this._historySeriesPlugin[historySeries.id];
      if (historySeriesPluginItem) {
        historySeriesTemp = historySeriesPluginItem(historySeries);
      }
      historySeriesArr.push(historySeriesTemp);
    });
    return historySeriesArr;
  }

  // 计算单周期的历史轨迹配置项
  protected _getHistoryOption(currentIndex: number) {
    const currentOption = this._historyOptions[currentIndex];
    // part1 从historySeriesTemplate 中计算出当前时间的series的data，并生成绘制连线的data数据
    const historySeriesArr = merge([], this._historySeriesTemplate);
    historySeriesArr.forEach(historySeriesItem => {
      // 截取数据
      historySeriesItem.data = historySeriesItem.data.slice(0, currentIndex + 1);
      // 筛掉异常值
      historySeriesItem.data = historySeriesItem.data.filter(d => d && d.value);
      // 添加绘制连线的信息
      historySeriesItem.data.forEach((historyDataItem, historyDataIdx) => {
        if (historySeriesItem.data[historyDataIdx + 1]) {
          historyDataItem.nextValue = historySeriesItem.data[historyDataIdx + 1].value;
        }
      });
    });

    // part2 处理非历史轨迹下的series
    currentOption.series.forEach((series, index) => {
      // 禁用交互
      series.silent = true;
      // 禁用临近搜索
      if (series.dvNearSearch) {
        series.dvNearSearch.enable = false;
      }
      // 禁用自动取消高亮
      if (series.dvAutoHide) {
        series.dvAutoHide.enable = false;
      }
      // 修改常态样式为blur态,此处需要直接修改数据中的itemStyle，如果修改series上的，可能优先级不够
      series.data.forEach(dataItem => {
        dataItem.itemStyle = [
          ...(dataItem.itemStyle || []),
          {
            id: 'circle',
            style: {
              fill: '#000000',
              opacity: 0.18,
              text: ''
            }
          }
        ];
      });

      // 隐藏label
      series.label = {
        show: false
      };

      // 关闭临近搜索
      series.dvNearSearch = {
        enable: false
      };
    });

    // part3 合并series并输出
    currentOption.series.push(...historySeriesArr);
    return currentOption;
  }

  // 高亮历史轨迹系列的最后一个点
  protected _highlightLastPoint(option: Option) {
    const lastPointsInfo = [];
    option.series.forEach((seriesItem, seriesIndex) => {
      if (!seriesItem.isHistory) {
        return;
      }
      if (seriesItem.data.length === 0) {
        return;
      }
      lastPointsInfo.push({
        seriesIndex,
        dataIndex: seriesItem.data.length - 1
      });
    });

    if (lastPointsInfo.length === 0) {
      return;
    }

    this._ecIns.dispatchAction({
      type: 'dvSelect',
      itemsInfo: lastPointsInfo
    });
  }

  // 获取自适应范围内所有散点的平铺数据
  _getAdaptFlatValue() {
    const flatValue = [];
    const currentIndex = this._timeline.getCurrentIndex();
    const optionTemp = this._isHistory
      ? this._historyOptions[currentIndex]
      : this._options[currentIndex];

    optionTemp.series.forEach(seriesItem => {
      if (!seriesItem.type === 'dvScatter') {
        return;
      }
      if (this._isHistory && !seriesItem.isHistory) {
        return;
      }
      seriesItem.data.forEach(dataItem => {
        flatValue.push(dataItem.value || dataItem);
      });
    });
    return flatValue;
  }

  // 自动自适应处理
  _autoAdaptHandle() {
    // 依赖historySeriesTemplate与historyOptions
    if (!this._historySeriesTemplate) {
      return;
    }
    const flatValue = [];
    this._historySeriesTemplate.forEach(seriesTemp => {
      const { data } = seriesTemp;
      data.forEach(dataItem => {
        if (dataItem) {
          flatValue.push(dataItem.value || dataItem);
        }
      });
    });

    this._historyAxisRange = containBubble(
      flatValue,
      this._dom,
      this._symbolSize || this._themeName,
      {
        xNiceRangeConfig: this._config.xNiceRangeConfig,
        yNiceRangeConfig: this._config.yNiceRangeConfig
      }
    );
    // 此处仅赋值，由enterHistory中的play函数生效该极值范围
    axisRangeHandle(this._historyOptions, this._historyAxisRange);
  }

  // 历史轨迹第一次播放完后高亮最后一个点
  _selectLastPoint() {
    clearTimeout(this._lastPointAnimationTimer);
    if (!this._isHistory) {
      return;
    }
    const currentIndex = this._timeline.getCurrentIndex();
    const historySeries = this._historyOptions[currentIndex].series.filter(
      seriesItem => seriesItem.isHistory
    );
    if (!historySeries.length) {
      return;
    }
    // 统计所有历史轨迹series的动画时长，并选出最长的时间
    const maxDataLength = Math.max(...historySeries.map(seriesItem => seriesItem.data.length));
    const animationTime = maxDataLength * STEP_ANIMATION_DURATION + NORMAL_ANIMATION_DURATION;
    this._lastPointAnimationTimer = setTimeout(() => {
      // 重新计算currentOption
      const currentIndexTemp = this._timeline.getCurrentIndex();
      const currentOptionTemp = this._historyOptions[currentIndexTemp];

      // 重置animationDelay
      this._chart.play({ option: optionDelayHandle(currentOptionTemp) });
      // 高亮所有系列的最后一个点
      this._highlightLastPoint(currentOptionTemp);
    }, animationTime);
  }

  // [ACTION]
  history() {
    this._isHistory ? this.exitHistory() : this.enterHistory();
  }

  /**
   * 打开历史轨迹
   * @param outSelectPoint 外部手动指定的选中点
   * @returns
   */
  enterHistory(outSelectPoint?: { seriesIndex: number; dataIndex: number }[]) {
    // 合并外部传入指定的高亮点
    this._selectPoint.push(...(outSelectPoint || []));

    if (!this._selectPoint.length) {
      console.warn('Missing the highlight points required to enable the historical track');
      return;
    }

    if (this._isHistory) {
      console.warn('Historical trajectory has been activated');
      return;
    }

    const currentIndex = this._timeline.getCurrentIndex();
    // 生成包含全周期历史数据的历史轨迹series 模板
    this._historySeriesTemplate = this._getHistorySeriesTemplate();
    // 生成全周期的history配置项
    const timelineIndexRange = this._timeline.getIndexRange();
    for (let i = timelineIndexRange[0]; i < timelineIndexRange[1]; i++) {
      this._historyOptions[i] = this._getHistoryOption(i);
    }

    if (this._config.autoAdapt) {
      // autoAdapt Handle, relay _historySeriesTemplate
      this._autoAdaptHandle();
    }
    const currentOption = this._historyOptions[currentIndex];
    this._chart.play({
      option: optionDelayHandle(currentOption, STEP_ANIMATION_DURATION)
    });
    // play后更新状态
    this._isHistory = true;
    // 清空历史轨迹的旧状态
    this._selectPoint = [];
    // 首次播放完毕后，重置animationDelay, 并高亮所有系列最后一个点
    this._selectLastPoint();
  }

  // 退出历史轨迹
  exitHistory() {
    if (!this._isHistory) {
      return;
    }
    clearTimeout(this._lastPointAnimationTimer);
    // 兜底代码，解除事件绑定

    if (this._config.autoAdapt) {
      this.exitAdapt(false);
    }
    this._chart.play({
      option: this._options[this._timeline.getCurrentIndex()],
      opts: {
        replaceMerge: ['series']
      }
    });
    this._ecIns.dispatchAction({
      type: 'dvUnselect'
    });
    // play后更新状态
    this._isHistory = false;
  }

  // 自适应
  enterAdapt(play = true) {
    // 计算自适应范围
    const adaptFlatValue = this._getAdaptFlatValue();
    if (!adaptFlatValue.length) {
      console.warn('Necessary data is missing within the current adaptive range');
      return;
    }
    const adaptRange = containBubble(
      adaptFlatValue,
      this._dom,
      this._symbolSize || this._themeName,
      {
        xNiceRangeConfig: this._config.xNiceRangeConfig,
        yNiceRangeConfig: this._config.yNiceRangeConfig
      }
    );

    // 应用给所有配置项

    // 退出历史轨迹不改变xRange, yRange
    // 自动自适应开启时在exitHistory中处理
    axisRangeHandle(this._options, adaptRange);
    if (this._isHistory) {
      axisRangeHandle(this._historyOptions, adaptRange);
    }

    if (play) {
      // 应用该范围
      const currentIndex = this._timeline.getCurrentIndex();
      this._chart.play({
        option: this._isHistory ? this._historyOptions[currentIndex] : this._options[currentIndex]
      });
    }
  }

  // 退出自适应
  exitAdapt(play = true) {
    axisRangeHandle(this._options, this._defaultAxisRange);
    if (this._isHistory) {
      axisRangeHandle(this._historyOptions, this._historyAxisRange);
    }

    if (play) {
      // 应用该范围
      const currentIndex = this._timeline.getCurrentIndex();
      this._chart.play({
        option: this._isHistory ? this._historyOptions[currentIndex] : this._options[currentIndex]
      });
    }
  }

  // 重置
  reset(option = ['history', 'adapt', 'dataZoom']) {
    if (option.includes('history') && this._isHistory) {
      this.exitHistory();
    }

    if (option.includes('adapt')) {
      this.exitAdapt();
    }
  }

  // [API]
  // getter&setter
  getHistorySeriesPlugin() {
    return this._historySeriesPlugin;
  }

  setHistorySeriesPluginById(id, fn: (...args: unknown[]) => unknown) {
    this._historySeriesPlugin[id] = fn;
  }
}
